import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo } from "react";

import { CollapsibleCard } from "@/components/elements/CollapsibleCard";
import { EMPTY_STR } from "@/constants/string";
import { Table } from "@/modules/irohana-components/elements/Table";
import { useTable } from "@/modules/irohana-components/hooks/useTable";
import {
  REFUND_ROW_COUNT,
  REFUND_TYPE,
  TENURE_OPERATOR,
  TENURE_OPERATOR_LABEL,
} from "@/features/organization/product/constants/productEnums";
import { TProductDetail, TRefundCondition } from "@/api/services/main/organization/product/dtos/product.dto";
import { formatMonths, formatRefund } from "@/features/organization/product/utils/productUtils";

type TProductRefundViewProps = {
  product: TProductDetail;
};

const emptyRefund = (index: number): TRefundCondition => ({
  id: `refund-empty-${index}`,
  tenureMonths: null,
  tenureOperator: TENURE_OPERATOR.UNDER,
  refundValue: null,
  refundType: REFUND_TYPE.PERCENT,
  order: index,
});

/** 返金条件設定 — read-only table (商品詳細). Always shows exactly 5 rows (padded with "-"). */
export const ProductRefundView: FC<TProductRefundViewProps> = ({ product }) => {
  // Fixed 5-row layout: take the first 5 conditions, pad the rest with empty ("-") rows.
  const rows = useMemo<TRefundCondition[]>(() => {
    const defined = product.refundConditions.slice(0, REFUND_ROW_COUNT);
    const padded = Array.from({ length: REFUND_ROW_COUNT - defined.length }, (_, i) => emptyRefund(i));
    return [...defined, ...padded];
  }, [product.refundConditions]);

  const columns = useMemo<MRT_ColumnDef<TRefundCondition>[]>(
    () => [
      {
        id: "tenure",
        header: "在職期間",
        Cell: ({ row }) =>
          row.original.tenureMonths == null
            ? EMPTY_STR.TEXT
            : `${formatMonths(row.original.tenureMonths)}${TENURE_OPERATOR_LABEL[row.original.tenureOperator]}`,
      },
      { id: "refund", header: "返金額（返金率）", size: 260, Cell: ({ row }) => formatRefund(row.original) },
    ],
    [],
  );

  const { table } = useTable({ rows, columns });

  return (
    <CollapsibleCard title="返金条件設定">
      <Table table={table} hiddenAddBtn />
    </CollapsibleCard>
  );
};
